import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PaymentLinkService } from 'src/app/domains/companies/services/payment-link.service';
import { UsersService } from 'src/app/domains/users/services';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { SendGridEmailService } from 'src/app/shared/services/sendgrid-email.service';
import { User, PaymentLinkData, UpdatePaymentUserData } from 'voxloud-types/dist/models';

@Component({
  selector: 'gdp-generate-update-payment-link-popup',
  templateUrl: './generate-update-payment-link-popup.component.html',
  styleUrls: ['./generate-update-payment-link-popup.component.scss']
})
export class GenerateUpdatePaymentLinkPopupComponent implements OnInit {
  public generatedLinkData: PaymentLinkData = null
  public emailList: string[] = [];
  public userList: UpdatePaymentUserData[] = [];
  public selectedEmail: string = this.emailList[0];
  public isManualEntry: boolean = false;
  public email: FormControl;
  public companyId: number;
  public companyEmail: string;
  public loading = {
    generateLink: false,
    sendEmail: false,
    loadingData: false
  }
  public locale: string;
  private language: string = 'it';
  private emailPattern = /^\w+([\.-]?\w+)+@\w+([\.:-]?\w+)+(\.[a-zA-Z0-9]{2,})+$/;
  public linkId: string;
  public hideGenerateLinkButton: boolean = false;
  public paymentLinkError: string | null = null;
  @Output() emitValue: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private activeModal: NgbActiveModal,
    private notificationService: NotificationService,
    private usersService: UsersService,
    private paymentLinkService: PaymentLinkService,
    private sendGridEmailService: SendGridEmailService
  ) {}

  ngOnInit(): void {
    this.locale = `${this.language}-${this.language?.toUpperCase()}`;
    this.fetchLinkData();
  }

  public fetchLinkData(): void {
    this.loading.loadingData = true;
    forkJoin({
      users: this.usersService.getUsers(this.companyId).pipe(
        catchError(() => {
          this.handleError();
          return of(undefined);
        })
      ),
      paymentLinkInfo: this.linkId ? this.paymentLinkService.getPaymentLinkInfo(this.linkId).pipe(
        catchError((error: any) => {
          this.handlePaymentLinkError(error);
          return of(undefined);
        })
      ) : of(undefined)
    }).subscribe({
      next: (response: {users: User[], paymentLinkInfo: PaymentLinkData}) => {
        this.userList = response.users.map(user => ({
          email: user.email,
          fullName: `${user.firstName} ${user.lastName}`
        }));

        this.emailList = this.userList.map(user => user.email);
        if(response.paymentLinkInfo) {
          this.handleGeneratedLinkData(response.paymentLinkInfo);
          this.hideGenerateLinkButton = false;
          this.paymentLinkError = null;
        }
        this.initForm();
        this.loading.loadingData = false;
      },
      error: () => {
        this.initForm();
        this.loading.loadingData = false;
      }
    })
  }

  private handleError(): void {
    this.initForm();
    this.loading.loadingData = false;
  }

  private handlePaymentLinkError(error: any): void {
    const errorMessage = error?.error?.message || error?.message || '';
    if (
      errorMessage.includes('has expired') ||
      errorMessage.includes('has already been used') || 
      errorMessage.includes('non-existent')
    ) {
      this.hideGenerateLinkButton = false;
      this.paymentLinkError = null;
    } else {
      this.paymentLinkError = 'company.overview.generate-update-payment-link.popup.notifications.fetching-link-error';
      this.hideGenerateLinkButton = true;
    }
    this.initForm();
    this.loading.loadingData = false;
  }

  private handleGeneratedLinkData(data: PaymentLinkData): void {
    this.generatedLinkData = {
      company_id: data.company_id,
      expires_at: data.expires_at,
      linkId: this.linkId,
      generated_link_token: data.generated_link_token
    }
  }

  private initForm(): void {
    if (!this.emailList.includes('Enter manually')) {
      this.emailList.push('Enter manually');
    }
    this.selectedEmail = this.emailList[0];
    this.email = new FormControl('', [
      Validators.required,
      Validators.pattern(this.emailPattern)
    ]);
  }

  public generateLink(): void {
    this.loading.generateLink = true;

    this.paymentLinkService.generatePaymentUpdateLink(this.companyId, this.companyEmail).subscribe({
      next: (response: PaymentLinkData) => {
        this.generatedLinkData = response;
        this.initForm();
        this.emitValue.emit();
        this.notificationService.success('company.overview.generate-update-payment-link.popup.notifications.generate-success');
        this.loading.generateLink = false;
      },
      error: () => {
        this.loading.generateLink = false;
      }
    });
  }

  public copyLink(): void {
    let tx = document.createElement('textarea');
    tx.value = this.generatedLinkData?.generated_link_token;
    document.body.appendChild(tx);
    tx.focus();
    tx.select();
    document.execCommand('copy');
    document.body.removeChild(tx);
    this.notificationService.success('company.overview.generate-update-payment-link.popup.notifications.copy-success');
  }

  public openLink(): void {
    if (this.generatedLinkData?.generated_link_token) {
      window.open(this.generatedLinkData.generated_link_token, '_blank');
    }
  }

  public selectEmail(email: string): void {
    this.selectedEmail = email;
    this.isManualEntry = email === 'Enter manually';
  }

  public onSubmit(): void {
    this.loading.sendEmail = true;
    const email = this.isManualEntry ? this.email.value : this.selectedEmail;
    const userName = this.userList.find((user) => user.email === email)?.fullName || null;
    this.sendGridEmailService.sendEmail({
      company_id: this.companyId,
      payment_link_id: this.generatedLinkData?.linkId || this.linkId,
      email,
      name: userName
    }).subscribe({
      next: () => {
        this.notificationService.success('company.overview.generate-update-payment-link.popup.notifications.send-success');
        this.loading.sendEmail = false;
        if (this.isManualEntry) {
          this.email.reset();
        }
      },
      error: () => {
        this.loading.sendEmail = false;
      }
    });
  }

  public get emailErrorMessage(): string {
    if (this.email.errors?.['required']) {
      return 'company.overview.generate-update-payment-link.popup.email.required';
    }
    if (this.email.errors?.['pattern']) {
      return 'company.overview.generate-update-payment-link.popup.email.invalid';
    }
    return '';
  }

  public close(): void {
    this.activeModal.close();
  }
}
